const BACKEND_URI = "";

import createClient from "openapi-fetch";
import type {
    AdminChatHistoryItem,
    AppConfig,
    Authorization,
    BuildInfo,
    ChatHistory,
    ChatHistoryItemDetails,
    ChatMessageAnalyticsResponseSchema,
    ChatRequest,
    ConversationAnalyticsResponseSchema,
    Domain,
    ExampleQuestions,
    FailureType,
    FeedbackAnalyticsResponseSchema,
    FeedbackItem,
    FeedbackList,
    FeedbackReason,
    FilterOptions,
    HistoryList,
    IdeaItem,
    IdeaList,
    IdeaReviewStatus,
    PatchFeedbackRequest,
    PatchIdeaRequest,
    paths,
    PeakHoursAnalyticsResponseSchema,
    SearchAdminRequest,
    SearchAdminResponse,
    SpeechRate,
    TouchpointAnalyticsResponseSchema,
    UserActivityAnalyticsResponseSchema,
    UserDetails,
    UserList,
    UserSettingsDocument
} from "./openapi.gen";
import { useLogin } from "../authConfig";

export async function getHeaders(idToken: string | undefined): Promise<Record<string, string>> {
    // If using login add the id token of the logged in account as the authorization
    if (useLogin) {
        if (idToken) {
            return { Authorization: `Bearer ${idToken}` };
        }
    }

    return {};
}

export class ApiError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = "ApiError";
        this.status = status;
    }
}

// Typed client for the backend's /v2/openapi.json: paths, parameters, bodies and
// responses all come from the generated spec types. List-valued query parameters
// are sent comma-separated, which is how the backend parses them.
const client = createClient<paths>({ baseUrl: BACKEND_URI, querySerializer: { array: { style: "form", explode: false } } });

// Throws an ApiError on a non-2xx response, prefixed with what was being attempted.
async function unwrap<T>(call: Promise<{ data?: T; response: Response }>, errorMessage: string): Promise<T> {
    const { data, response } = await call;
    if (!response.ok) {
        throw new ApiError(response.status, `${errorMessage}: ${response.statusText || response.status}`);
    }
    return data as T;
}

// Optional text filters are left out of the query when blank.
const nonBlank = (value: string | undefined): string | undefined => (value && value.trim() ? value.trim() : undefined);

export async function configApi(): Promise<AppConfig> {
    return unwrap(client.GET("/config"), "Getting config failed");
}

export async function versionApi(): Promise<BuildInfo> {
    return unwrap(client.GET("/version"), "Getting version failed");
}

export async function getSpeechApi(text: string, speechRate: SpeechRate): Promise<string | null> {
    const { data, response } = await client.POST("/speech", { body: { text, speechRate }, parseAs: "blob" });
    if (response.status == 200 && data) {
        return URL.createObjectURL(data);
    } else if (response.status == 400) {
        console.log("Speech synthesis is not enabled.");
    } else {
        console.error("Unable to get speech synthesis.");
    }
    return null;
}

export function getSstWebSocketUrl(idToken: string, sampleRate: number): string {
    // Browsers can't set an Authorization header on a WebSocket handshake,
    // so the bearer token is passed via the ?token= query fallback.
    const base = BACKEND_URI || window.location.origin;
    const wsBase = base.replace(/^http/, "ws");
    const params = new URLSearchParams({ token: idToken, sampleRate: String(sampleRate) });
    return `${wsBase}/v2/sst/wss?${params}`;
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

export async function getChatHistoryListApi(count: number, continuationToken: string | undefined, idToken: string, domain?: Domain): Promise<ChatHistory> {
    return unwrap(
        client.POST("/v2/chat/history", {
            headers: await getHeaders(idToken),
            body: { count, continuationToken: continuationToken ?? null, ...(domain ? { domain } : {}) }
        }),
        "Getting chat histories failed"
    );
}

export async function getChatHistoryApi(id: string, idToken: string): Promise<ChatHistoryItemDetails> {
    return unwrap(
        client.POST("/v2/chat/history/{conversation_id}", { headers: await getHeaders(idToken), params: { path: { conversation_id: id } } }),
        "Getting chat history failed"
    );
}

export async function deleteChatHistoryApi(id: string, idToken: string): Promise<void> {
    await unwrap(
        client.DELETE("/v2/chat/history/{conversation_id}", { headers: await getHeaders(idToken), params: { path: { conversation_id: id } } }),
        "Deleting chat history failed"
    );
}

export async function getUserSettingsApi(idToken: string): Promise<UserSettingsDocument> {
    const { settings } = await unwrap(client.GET("/usersettings", { headers: await getHeaders(idToken) }), "Getting user settings failed");
    return settings;
}

export async function getAuthorizationApi(idToken: string, signal?: AbortSignal): Promise<Authorization> {
    return unwrap(client.GET("/authorization", { headers: await getHeaders(idToken), signal }), "Getting authorization failed");
}

export async function postV2FeedbackApi(
    conversationId: string,
    positive: boolean,
    turnIndex: number,
    comment: string | undefined,
    idToken: string,
    reason?: FeedbackReason
): Promise<void> {
    await unwrap(
        client.POST("/v2/chat/feedback/{conversation_id}", {
            headers: await getHeaders(idToken),
            params: { path: { conversation_id: conversationId } },
            body: { positive, turnIndex, comment, reason }
        }),
        "Posting v2 feedback failed"
    );
}

export async function getFeedbackApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    startDate?: string,
    endDate?: string,
    userId?: string,
    timezone?: string, // IANA timezone string
    reviewStatus?: "reviewed" | "not_reviewed" | "all",
    failureTypes?: FailureType[],
    signal?: AbortSignal
): Promise<FeedbackList> {
    return unwrap(
        client.GET("/feedbacklist", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    page,
                    page_size: pageSize,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                    search: nonBlank(search),
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    user_id: nonBlank(userId),
                    timezone: timezone || undefined,
                    review_status: reviewStatus && reviewStatus !== "all" ? reviewStatus : undefined,
                    failure_types: failureTypes && failureTypes.length > 0 ? failureTypes : undefined
                }
            },
            signal
        }),
        "Getting user feedback failed"
    );
}

export async function getFeedbackByIdApi(id: string, idToken: string): Promise<FeedbackItem> {
    return unwrap(
        client.GET("/feedback/{feedback_id}", { headers: await getHeaders(idToken), params: { path: { feedback_id: id } } }),
        "Getting feedback failed"
    );
}

export async function deleteFeedbackItemApi(id: string, idToken: string): Promise<void> {
    await unwrap(
        client.DELETE("/feedback/{feedback_id}", { headers: await getHeaders(idToken), params: { path: { feedback_id: id } } }),
        "Deleting feedback item failed"
    );
}

async function patchFeedbackApi(id: string, body: PatchFeedbackRequest, errorMessage: string, idToken: string): Promise<FeedbackItem> {
    return unwrap(client.PATCH("/feedback/{feedback_id}", { headers: await getHeaders(idToken), params: { path: { feedback_id: id } }, body }), errorMessage);
}

export async function updateFeedbackJiraTicketApi(id: string, jiraTicket: string | null, idToken: string): Promise<FeedbackItem> {
    return patchFeedbackApi(id, { jira_ticket: jiraTicket }, "Updating feedback failed", idToken);
}

export async function updateFeedbackReviewStatusApi(id: string, reviewStatus: "reviewed" | null, idToken: string): Promise<FeedbackItem> {
    return patchFeedbackApi(id, { review_status: reviewStatus }, "Updating feedback review status failed", idToken);
}

export async function updateFeedbackFailureTypeApi(id: string, failureType: FailureType | null, idToken: string): Promise<FeedbackItem> {
    return patchFeedbackApi(id, { failure_type: failureType }, "Updating feedback failure type failed", idToken);
}

export async function updateFeedbackCommentApi(id: string, comment: string | null, idToken: string): Promise<FeedbackItem> {
    return patchFeedbackApi(id, { comment }, "Updating feedback comment failed", idToken);
}

export async function exportFeedbackCsvApi(
    idToken: string,
    start?: string, // YYYY-MM-DD
    end?: string, // YYYY-MM-DD
    userId?: string,
    timezone?: string // IANA timezone string
): Promise<Blob> {
    return unwrap(
        client.GET("/feedback/export", {
            headers: await getHeaders(idToken),
            params: { query: { start_date: start || undefined, end_date: end || undefined, user_id: nonBlank(userId), timezone: timezone || undefined } },
            parseAs: "blob"
        }),
        "Exporting feedback failed"
    );
}

type AnalyticsAggregation = "day" | "week" | "month" | "year";

// Filters shared by the analytics endpoints; lists are sent joined, as the backend expects.
function analyticsFilters(countries?: string[], touchpoints?: string[], entraOid?: string, domains?: string[]) {
    return {
        countries: countries && countries.length > 0 ? countries.join(",") : undefined,
        // Use triple pipe delimiter to avoid conflicts with commas in user-agent strings
        touchpoints: touchpoints && touchpoints.length > 0 ? touchpoints.join("|||") : undefined,
        entra_oid: entraOid || undefined,
        domains: domains && domains.length > 0 ? domains.join(",") : undefined
    };
}

export async function getFeedbackAnalyticsApi(
    idToken: string,
    aggregation: AnalyticsAggregation,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<FeedbackAnalyticsResponseSchema> {
    return unwrap(
        client.GET("/analytics/feedback", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    aggregation,
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    timezone: timezone || undefined,
                    ...analyticsFilters(countries, touchpoints, entraOid, domains)
                }
            }
        }),
        "Getting feedback analytics failed"
    );
}

export async function getConversationsAnalyticsApi(
    idToken: string,
    aggregation: AnalyticsAggregation,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<ConversationAnalyticsResponseSchema> {
    return unwrap(
        client.GET("/analytics/conversations", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    aggregation,
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    timezone: timezone || undefined,
                    ...analyticsFilters(countries, touchpoints, entraOid, domains)
                }
            }
        }),
        "Getting conversations analytics failed"
    );
}

export async function getChatMessagesAnalyticsApi(
    idToken: string,
    aggregation: AnalyticsAggregation,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<ChatMessageAnalyticsResponseSchema> {
    return unwrap(
        client.GET("/analytics/chat-messages", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    aggregation,
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    timezone: timezone || undefined,
                    ...analyticsFilters(countries, touchpoints, entraOid, domains)
                }
            }
        }),
        "Getting chat messages analytics failed"
    );
}

export async function getPeakHoursAnalyticsApi(
    idToken: string,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<PeakHoursAnalyticsResponseSchema> {
    return unwrap(
        client.GET("/analytics/peak-hours", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    timezone: timezone || undefined,
                    ...analyticsFilters(countries, touchpoints, entraOid, domains)
                }
            }
        }),
        "Getting peak hours analytics failed"
    );
}

export async function exportHistoryApi(
    idToken: string,
    start?: string, // YYYY-MM-DD
    end?: string, // YYYY-MM-DD
    userId?: string,
    timezone?: string // IANA timezone string
): Promise<Blob> {
    return unwrap(
        client.GET("/history/export", {
            headers: await getHeaders(idToken),
            params: { query: { start_date: start || undefined, end_date: end || undefined, user_id: userId || undefined, timezone: timezone || undefined } },
            parseAs: "blob"
        }),
        "Exporting history failed"
    );
}

export async function getAdminChatHistoryListApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    startDate?: string,
    endDate?: string,
    userId?: string,
    timezone?: string, // IANA timezone string
    signal?: AbortSignal
): Promise<HistoryList> {
    return unwrap(
        client.GET("/history/list", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    page,
                    page_size: pageSize,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                    search: nonBlank(search),
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    user_id: userId || undefined,
                    timezone: timezone || undefined
                }
            },
            signal
        }),
        "Getting user history failed"
    );
}

export async function getChatHistoryItemAdminApi(id: string, idToken: string): Promise<AdminChatHistoryItem> {
    return unwrap(
        client.GET("/history/items/{item_id}", { headers: await getHeaders(idToken), params: { path: { item_id: id } } }),
        "Getting admin chat history failed"
    );
}

export async function deleteChatHistoryItemAdminApi(id: string, entraOid: string, idToken: string): Promise<void> {
    await unwrap(
        client.DELETE("/history/items/{item_id}", {
            headers: await getHeaders(idToken),
            params: { path: { item_id: id }, query: { entra_oid: entraOid } }
        }),
        "Deleting chat history item failed"
    );
}

export async function getChatHistoryItemDatabricksTraceUrlApi(id: string, idToken: string): Promise<string> {
    const { url } = await unwrap(
        client.GET("/history/items/{item_id}/databricks-trace-url", { headers: await getHeaders(idToken), params: { path: { item_id: id } } }),
        "Getting Databricks trace link failed"
    );
    return url;
}

export async function getUsersListApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy?: string,
    sortDir?: "asc" | "desc",
    search?: string,
    signal?: AbortSignal
): Promise<UserList> {
    return unwrap(
        client.GET("/users/list", {
            headers: await getHeaders(idToken),
            params: { query: { page, page_size: pageSize, sort_by: sortBy || undefined, sort_dir: sortDir || undefined, search: search || undefined } },
            signal
        }),
        "Getting users list failed"
    );
}

export async function getUserDetailsApi(idToken: string, userId: string): Promise<UserDetails> {
    return unwrap(client.GET("/users/{user_id}", { headers: await getHeaders(idToken), params: { path: { user_id: userId } } }), "Getting user details failed");
}

export async function exportUsersApi(idToken: string): Promise<Blob> {
    return unwrap(client.GET("/users/export", { headers: await getHeaders(idToken), parseAs: "blob" }), "Export failed");
}

export async function getUserActivityAnalyticsApi(
    idToken: string,
    topUsers: number = 10,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    countries?: string[], // Selected countries filter
    touchpoints?: string[], // Selected touchpoints filter
    entraOid?: string, // Filter for specific user
    domains?: string[] // Selected domains filter
): Promise<UserActivityAnalyticsResponseSchema> {
    return unwrap(
        client.GET("/analytics/user-activity", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    top_users: topUsers,
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    ...analyticsFilters(countries, touchpoints, entraOid, domains)
                }
            }
        }),
        "Getting user activity analytics failed"
    );
}

export async function getTouchpointAnalyticsApi(
    idToken: string,
    startDate?: string, // ISO string
    endDate?: string, // ISO string
    timezone?: string, // IANA timezone string
    countries?: string[],
    entraOid?: string,
    domains?: string[]
): Promise<TouchpointAnalyticsResponseSchema> {
    return unwrap(
        client.GET("/analytics/touchpoints", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    timezone: timezone || undefined,
                    ...analyticsFilters(countries, undefined, entraOid, domains)
                }
            }
        }),
        "Getting touchpoint analytics failed"
    );
}

// Returns the raw response: its body is an NDJSON stream of Delta lines, and the
// caller inspects the status itself.
export async function chatV2StreamApi(request: ChatRequest, idToken: string | undefined, signal: AbortSignal): Promise<globalThis.Response> {
    const { response } = await client.POST("/v2/chat/stream", { headers: await getHeaders(idToken), body: request, signal, parseAs: "stream" });
    return response;
}

export async function getExampleQuestionsApi(idToken: string | undefined, language: string, domain: Domain): Promise<ExampleQuestions> {
    return unwrap(
        client.GET("/v2/examplequestion", {
            headers: { ...(await getHeaders(idToken)), "Accept-Language": language },
            params: { query: { domain } }
        }),
        "Getting example questions failed"
    );
}

export async function getFilterOptionsApi(idToken: string | undefined, language: string, domain: Domain): Promise<FilterOptions> {
    return unwrap(
        client.GET("/v2/chat/userfilter", {
            headers: { ...(await getHeaders(idToken)), "Accept-Language": language },
            params: { query: { domain } }
        }),
        "Getting filter options failed"
    );
}

export async function searchAdminApi(searchRequest: SearchAdminRequest, idToken: string | undefined): Promise<SearchAdminResponse> {
    return unwrap(client.POST("/admin_ai_search/search", { headers: await getHeaders(idToken), body: searchRequest }), "Azure AI Search admin request failed");
}

export async function getIdeasApi(
    idToken: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDir: "asc" | "desc",
    search?: string,
    startDate?: string,
    endDate?: string,
    userId?: string,
    timezone?: string,
    categoryFilter?: string,
    includeDrafts?: boolean,
    reviewStatus?: IdeaReviewStatus | "all",
    signal?: AbortSignal
): Promise<IdeaList> {
    return unwrap(
        client.GET("/idealist", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    page,
                    page_size: pageSize,
                    sort_by: sortBy,
                    sort_dir: sortDir,
                    search: nonBlank(search),
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                    user_id: nonBlank(userId),
                    timezone: timezone || undefined,
                    category_filter: nonBlank(categoryFilter),
                    include_drafts: includeDrafts || undefined,
                    review_status: reviewStatus && reviewStatus !== "all" ? reviewStatus : undefined
                }
            },
            signal
        }),
        "Getting ideas failed"
    );
}

export async function getIdeaByIdApi(id: string, idToken: string): Promise<IdeaItem> {
    return unwrap(client.GET("/idea/{idea_id}", { headers: await getHeaders(idToken), params: { path: { idea_id: id } } }), "Getting idea failed");
}

async function patchIdeaApi(id: string, body: PatchIdeaRequest, errorMessage: string, idToken: string): Promise<IdeaItem> {
    return unwrap(client.PATCH("/idea/{idea_id}", { headers: await getHeaders(idToken), params: { path: { idea_id: id } }, body }), errorMessage);
}

export async function updateIdeaReviewStatusApi(id: string, reviewStatus: IdeaReviewStatus, idToken: string): Promise<IdeaItem> {
    return patchIdeaApi(id, { review_status: reviewStatus }, "Updating idea review status failed", idToken);
}

export async function updateIdeaJiraLinkApi(id: string, jiraLink: string | null, idToken: string): Promise<IdeaItem> {
    return patchIdeaApi(id, { jira_link: jiraLink }, "Updating idea Jira link failed", idToken);
}

export async function updateIdeaReviewCommentApi(id: string, reviewComment: string | null, idToken: string): Promise<IdeaItem> {
    return patchIdeaApi(id, { review_comment: reviewComment }, "Updating idea review comment failed", idToken);
}

export async function exportIdeasApi(
    idToken: string,
    start?: string,
    end?: string,
    userId?: string,
    timezone?: string,
    reviewStatus?: IdeaReviewStatus | "all"
): Promise<Blob> {
    return unwrap(
        client.GET("/idea/export", {
            headers: await getHeaders(idToken),
            params: {
                query: {
                    start_date: start || undefined,
                    end_date: end || undefined,
                    user_id: nonBlank(userId),
                    timezone: timezone || undefined,
                    review_status: reviewStatus && reviewStatus !== "all" ? reviewStatus : undefined
                }
            },
            parseAs: "blob"
        }),
        "Exporting ideas failed"
    );
}
